"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { v4 as uuidv4 } from "uuid";
import {
  ArrowRight,
  Eye,
  Loader2,
  Repeat2,
  Sparkles,
  Zap,
  BarChart3,
  Layers,
  Globe,
  LayoutTemplate,
  Users,
  CalendarDays,
  KanbanSquare,
  Bot,
  MessageCircle,
  Briefcase,
  Code2,
} from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { signOutCurrentDevice } from "@/lib/auth/logout";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { LoginModal } from "@/components/auth/login-modal";
import { SignupModal } from "@/components/auth/signup-modal";
import { clearPendingAuth, useAuthModalSync } from "@/lib/pending-auth";
import { trackTemplateUsed } from "@/lib/analytics";
import { customToast } from "@/lib/toast";
import {
  cloneErrorMessage,
  isRetryableCloneError,
} from "@/lib/clone/error-copy";

// Mirrors the `templates` table columns (see app/api/templates/route.ts). Each
// row is a real, published template with a starter_prompt we load into the
// home hero.
interface DBTemplate {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string | null;
  icon_name: string | null;
  screenshot_url: string | null;
  starter_prompt: string | null;
  source_project_id: string | null;
  use_count: number;
  sort_order: number;
}

// icon_name → lucide icon for templates without a screenshot. Same set the
// homepage uses so a template's placeholder art is consistent across surfaces.
const ICON_BY_NAME: Record<string, typeof Code2> = {
  Sparkles,
  Zap,
  BarChart3,
  Layers,
  Globe,
  LayoutTemplate,
  Users,
  CalendarDays,
  KanbanSquare,
  Bot,
  MessageCircle,
  Briefcase,
  Code2,
};

// "Browse all templates" destination. Build-first: every card's PRIMARY action
// starts a project rather than opening a passive browse view.
//
// Which action depends on whether the template has a clonable source. A card
// showing a real screenshot of a real project must actually clone that project
// ("Clone this"); a card that only carries a starter prompt must say so ("Use
// this prompt") and prefill the home hero. Promising a screenshot and then
// re-generating it from text is the bug this split exists to prevent.
export default function TemplatesPage() {
  const router = useRouter();
  const [user, setUser] = useState<{
    email?: string;
    userMetadata?: Record<string, unknown>;
  } | null>(null);
  const [templates, setTemplates] = useState<DBTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // Non-null while a clone is in flight: locks every card's action so a
  // double-click can't create two projects and spend two credits.
  const [cloningSlug, setCloningSlug] = useState<string | null>(null);
  // Auth modals for the header's "Log in" row (mobile menu). Same pair the
  // home/pricing/community pages mount, so signing in never bounces a visitor
  // off the templates grid.
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  // Pre-fill for the signup modal's email step, set only by the login modal's
  // "No account found — Sign up" handoff. Cleared on close so it never leaks
  // into a later, unrelated signup open.
  const [signupInitialEmail, setSignupInitialEmail] = useState("");

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        setUser({
          email: data.user.email,
          userMetadata: data.user.user_metadata ?? {},
        });
      }
    });
  }, []);

  // Reopen the auth modal on return from a 3rd-party OAuth provider
  // (Google/Facebook). Handles both real loads and bfcache restores.
  useAuthModalSync(loginOpen, signupOpen, setLoginOpen, setSignupOpen);

  // Keep the header in step with an in-page sign-in: adopt the new session so
  // the menu swaps to the signed-in set, and drop any stale pending-auth flag
  // so the modal doesn't reopen later.
  useEffect(() => {
    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN") return;
      clearPendingAuth();
      setLoginOpen(false);
      setSignupOpen(false);
      if (session?.user) {
        setUser({
          email: session.user.email,
          userMetadata: session.user.user_metadata ?? {},
        });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/templates")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data.templates)) setTemplates(data.templates);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLogout() {
    const supabase = createClient();
    await signOutCurrentDevice(supabase);
    setUser(null);
    router.push("/");
  }

  // Load this template's starter prompt into the home hero (prefill, NOT an
  // auto-build) and navigate there so the visitor can review/edit then hit
  // "Build my app". `?prefill=` is the home-page handoff param. Only used for
  // templates with NO clonable source. (Named handle* — NOT use* — so it isn't
  // mistaken for a React Hook.)
  const handleUsePrompt = (tpl: DBTemplate) => {
    const prompt = (tpl.starter_prompt || tpl.description || "").trim();
    if (!prompt) return;
    trackTemplateUsed(tpl.slug, "templates_page");
    router.push(`/?prefill=${encodeURIComponent(prompt)}`);
  };

  // Clone the template's real source project. `/use` resolves source_project_id
  // and copies its files, so the new project opens as the screenshot showed
  // rather than being re-imagined from the starter prompt.
  const handleCloneTemplate = async (tpl: DBTemplate) => {
    if (cloningSlug) return;
    trackTemplateUsed(tpl.slug, "templates_page");
    if (!user) {
      // Hand the visitor to this template's own page, which owns the signup
      // modal and the same clone button — so the chosen source survives auth
      // without a second intent stash.
      router.push(`/templates/${tpl.slug}`);
      return;
    }
    setCloningSlug(tpl.slug);
    try {
      const res = await fetch(`/api/templates/${tpl.slug}/use`, {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data?.error === "project_limit_reached") {
        router.push(`/project/${uuidv4()}?show_limit=true`);
        return;
      }
      if (res.status === 403 && data?.error === "email_verification_required") {
        // The global gate modal (mounted in app/layout.tsx) already explains
        // this; a second, vaguer toast on top of it just contradicts it.
        setCloningSlug(null);
        return;
      }
      if (res.status === 402) {
        customToast.error("You're out of credits. Top up to keep building.", {
          action: { label: "Upgrade", onClick: () => router.push("/pricing") },
        });
        setCloningSlug(null);
        return;
      }
      if (res.status === 422) {
        // The route sends the reason with the code — a template whose source has
        // vanished and one the health gate refused are both 422 but need
        // different copy, and only the server knows which one this is.
        customToast.error(
          data?.message ||
            "This template's files are no longer available, so it can't be cloned.",
        );
        setCloningSlug(null);
        return;
      }
      // 503 = the lookup itself failed (ours, retryable); 404 = the template is
      // gone or unpublished (permanent). Both used to fall into the catch below
      // and read as the same "try again" — right for one, wrong for the other.
      if (!res.ok) {
        const code: string | undefined = data?.error;
        customToast.error(
          cloneErrorMessage(
            code,
            data?.message,
            "Couldn't clone this template. Try again.",
          ),
          isRetryableCloneError(code, res.status)
            ? {
                action: {
                  label: "Retry",
                  onClick: () => void handleCloneTemplate(tpl),
                },
              }
            : undefined,
        );
        setCloningSlug(null);
        return;
      }
      if (!data?.id) throw new Error(data?.error || "clone_failed");
      // ?from=template → template loading copy on first open (see load-kind).
      router.push(`/project/${data.id}?from=template`);
    } catch (err) {
      console.error("Clone template error:", err);
      customToast.error("Couldn't clone this template. Try again.");
      setCloningSlug(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader
        user={user}
        onLogout={handleLogout}
        activePage="templates"
        onLogin={() => setLoginOpen(true)}
      />

      {/* Hero */}
      <section className="border-b border-border px-4 py-14 text-center">
        <div className="mx-auto max-w-3xl">
          {/* Green to match the homepage templates section's eyebrow — the
              two surfaces are the same journey and used to disagree twice
              over: this one was flat `orange-500` while the homepage's was
              #FF5A1F, so they were not even the same orange. 12px is below
              the WCAG large-text cutoff, hence emerald-700/400 (5.5:1 light,
              10.3:1 dark) rather than a lighter step. */}
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-400">
            Templates
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
            Start from a template
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Pick one and Panda copies its code into a project of your own — then
            build it your way.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 9 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-card p-5 shadow-sm"
                >
                  <div className="mb-4 h-40 w-full animate-pulse rounded-xl bg-muted" />
                  <div className="mb-3 h-5 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="mb-4 h-3 w-1/2 animate-pulse rounded bg-muted" />
                  <div className="h-11 w-full animate-pulse rounded-xl bg-muted" />
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <p className="py-20 text-center text-muted-foreground">
              No templates available yet.
            </p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map((tpl) => {
                const Icon =
                  (tpl.icon_name && ICON_BY_NAME[tpl.icon_name]) ||
                  LayoutTemplate;
                return (
                  <div
                    key={tpl.id}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="h-40 border-b border-border">
                      {tpl.screenshot_url ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={tpl.screenshot_url}
                          alt={tpl.name}
                          className="h-full w-full object-cover object-top"
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center bg-muted text-muted-foreground/50">
                          <Icon className="h-9 w-9" aria-hidden />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="text-base font-semibold tracking-tight">
                        {tpl.name}
                      </h3>
                      {tpl.description && (
                        <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">
                          {tpl.description}
                        </p>
                      )}
                      <div className="mt-auto flex flex-col gap-2 pt-4">
                        {/* Only a template with a real source project may offer
                            "Clone this" — anything else would imply the card's
                            screenshot is reproducible from its prompt alone.

                            Success gradient, not the brand one. Both labels
                            this button can carry ("Clone this", "Use this
                            prompt") move the visitor forward and can simply be
                            repeated, which is the green case in
                            components/ui/modal-accents.ts. It was
                            #FF5606→#AA0346 — the same paint as a destructive
                            confirm — on the highest-intent control of the
                            page. Geometry and motion are unchanged; only the
                            hue and the focus ring moved. */}
                        <button
                          type="button"
                          // Lock every clone-capable card while one clone is in
                          // flight (each spends a credit). Prefill cards cost
                          // nothing and navigate synchronously, so they stay live.
                          disabled={
                            !!tpl.source_project_id && cloningSlug !== null
                          }
                          aria-busy={cloningSlug === tpl.slug}
                          onClick={() =>
                            tpl.source_project_id
                              ? void handleCloneTemplate(tpl)
                              : handleUsePrompt(tpl)
                          }
                          className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-panda-gradient-success text-sm font-semibold text-white shadow-md shadow-emerald-900/25 transition-all hover:-translate-y-px hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        >
                          {cloningSlug === tpl.slug ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                              Cloning…
                            </>
                          ) : tpl.source_project_id ? (
                            <>
                              <Repeat2 className="h-4 w-4" aria-hidden />
                              Clone this
                            </>
                          ) : (
                            <>
                              Use this prompt
                              <ArrowRight className="h-4 w-4" aria-hidden />
                            </>
                          )}
                        </button>
                        <Link
                          href={`/templates/${tpl.slug}`}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg py-1 text-center text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        >
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                          Preview
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <AppFooter />

      <LoginModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        next="/templates"
        onSwitchToSignup={(email) => {
          setLoginOpen(false);
          setSignupInitialEmail(email);
          setSignupOpen(true);
        }}
      />
      <SignupModal
        open={signupOpen}
        onOpenChange={(o) => {
          setSignupOpen(o);
          if (!o) setSignupInitialEmail("");
        }}
        next="/templates"
        initialEmail={signupInitialEmail}
        source="templates_nav_signup"
      />
    </div>
  );
}
